import axios from 'axios';

import { ActionType, IChallengeImage } from 'interfaces';
import { MediaObjectType } from 'interfaces/mediaObject.types';
import { ROUTES } from '../constants';

export const API_ROOT =
  process.env.NODE_ENV === 'production'
    ? `${window.location.protocol}//${window.location.host}`
    : process.env.REACT_APP_HOST;

axios.defaults.withCredentials = true;

export const api = axios.create({
  baseURL: `${API_ROOT}/v2`,
  timeout: 0,
  headers: {
    'content-type': 'application/ld+json',
  },
});

export const REQUEST = 'REQUEST';
export const SUCCESS = 'SUCCESS';
export const FAILURE = 'FAILURE';

export interface RequestTypes {
  REQUEST: string;
  SUCCESS: string;
  FAILURE: string;
}

export const createRequestTypes = (base: string): RequestTypes => ({
  REQUEST: `${base}_${REQUEST}`,
  SUCCESS: `${base}_${SUCCESS}`,
  FAILURE: `${base}_${FAILURE}`,
});

export const action = (type: string, payload = {}): ActionType => ({
  type,
  payload,
});

export const formatApiURI = (partialURI: string) =>
  `${API_ROOT}/v2/${partialURI}`;

/**
 * TODO: media utils has been created with getMediaObjectURI copy,
 * remove this one and fix all components
 */
export const getMediaObjectURI = (
  partialUri:
    | string
    | number
    | MediaObjectType
    | IChallengeImage
    | undefined
    | null = '',
): string => {
  if (!partialUri) {
    console.warn('No parameter received for getMediaObjectURI'); // eslint-disable-line no-console
    return '';
  }
  if (typeof partialUri === 'number' || !isNaN(Number(partialUri))) {
    // eslint-disable-line no-restricted-globals
    return `${API_ROOT}/v2/media-objects/${partialUri}`;
  }
  if (typeof partialUri === 'string') {
    if (partialUri.indexOf('/v2/media-objects/') === 0) {
      return `${API_ROOT}${partialUri}`;
    }
    return `${API_ROOT}/v2/media-objects/${partialUri}`;
  }
  if (partialUri['@id']) {
    return `${API_ROOT}${partialUri['@id']}`;
  }
  if (partialUri.id) {
    return `${API_ROOT}/v2/media-objects/${partialUri.id}`;
  }

  console.error(
    // eslint-disable-line no-console
    'The received parameter for getMediaObjectURI does not have the expected format:',
    `"${partialUri}"`,
    'Expected either a string or a number or a MediaObjectType',
  );
  return '';
};

// Conservé pour compatibilité — le token JWT est désormais géré en cookie HttpOnly
// par le serveur (max_api_token). Cette fonction ne pose plus d'Authorization header.
export const setAuthToken = (_token?: string): void => {};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const redirectPathname = `${ROUTES.BASENAME}${ROUTES.LOGIN}`;

      // Toutes les sous-routes de /fo/auth (subscribe, forgot-password,
      // reset-password, create/password, link-expired, sso-failure…) sont
      // publiques et atteintes par un visiteur anonyme, dont le GET /me au
      // montage renvoie un 401 légitime. Ce 401 ne signifie pas « session
      // expirée » : il ne doit pas provoquer de redirection pleine page, qui
      // réinitialiserait le router et masquerait la sous-route demandée.
      // Le suffixe '/' borne le préfixe pour ne pas capturer /fo/authXXX.
      const { pathname } = window.location;
      const isAlreadyOnAuthRoute =
        pathname === redirectPathname ||
        pathname.startsWith(`${redirectPathname}/`);

      if (!isAlreadyOnAuthRoute) {
        // Propager la route demandée pour y revenir après login (même
        // construction que PrivateRoute). La home est déjà la cible par
        // défaut post-login : pas de returnPath pour elle.
        const isHomeRoute =
          pathname === ROUTES.BASENAME ||
          pathname === `${ROUTES.BASENAME}${ROUTES.HOME}`;
        const returnPath = `${pathname}${window.location.search}`;

        window.location.replace(
          isHomeRoute
            ? redirectPathname
            : `${redirectPathname}?returnPath=${encodeURIComponent(returnPath)}`,
        );
      }
    }

    // Token first-login expiré → le back retourne 410 avec un resendToken
    if (error?.response?.status === 410) {
      const resendToken = error.response.data?.resendToken || '';
      window.location.replace(
        `${ROUTES.BASENAME}${ROUTES.LINK_EXPIRED}${resendToken ? `?resendToken=${resendToken}` : ''}`,
      );
    }

    return Promise.reject(error);
  },
);
